/**
 * LiveActivityPanel — M-X11 (2026-05-22).
 *
 * Founder requirement (2026-05-21, translated from RU): while the AI agent
 * is working I want to see, inside the chat session, some animation showing
 * it is really working. Something must monitor that it hasn't hung, that the
 * process is moving, that it is really working. And show the time elapsed
 * since launch ... in minutes and seconds. And how many tokens it burned,
 * including sub-agents and everyone else.
 *
 * Layout:
 *   ┌─────────────────────────────────────────────────────────┐
 *   │ ● Agent working…   2:34   28.5k tokens   ▶ Details     │
 *   │   ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ │
 *   └─────────────────────────────────────────────────────────┘
 *
 * Expanded view: tree of sub-agents (Anthropic only), recent tool calls
 * list (clickable for full preview), run metadata.
 *
 * Data sources:
 *   - SSE events `run_started` / `run_progress` / `usage` /
 *     `tool_use_started` / `tool_use_completed` / `subagent_started` /
 *     `subagent_completed` from the gateway (M-X11 server-side patch).
 *   - On reattach: hydrates state from `sessionRunsApi.listActive` —
 *     the row carries cumulative counters from the DB heartbeat.
 *
 * The panel renders only when `state.runId !== null && state.live`. On
 * stream `done`, the parent decides whether to hide it immediately
 * (collapsed by default).
 *
 * Animation states:
 *   - green pulse: agent working, last progress event within 10s
 *   - amber pulse: thinking phase (currentActivity === 'Thinking…')
 *   - solid red, no pulse: stale (no progress >10s) — something stuck
 *   - grey static: panel idle / done
 */
import { effortLabel } from '../../lib/agent/effort-levels';
import * as React from 'react';
import {
  Bot,
  ChevronDown,
  ChevronRight,
  Clock,
  Cpu,
  GitBranch,
  Hammer,
  Loader2,
  Wrench,
} from 'lucide-react';

import { cn } from '../../lib/utils';
import { CopyButton } from '../ui/copy-button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';
import type { SessionRunInfo } from '../../lib/api/session-runs';

// ── Public types ──────────────────────────────────────────────────────

export interface ToolCallEntry {
  id: string;
  name: string;
  inputPreview: string;
  status: 'running' | 'ok' | 'error';
  startedAtMs: number;
  endedAtMs: number | null;
}

export interface SubAgentEntry {
  parentToolUseId: string;
  subagentType: string | null;
  description: string | null;
  status: 'running' | 'ok' | 'error';
  startedAtMs: number;
  endedAtMs: number | null;
  /** M-X11-fix: per-sub-agent token accumulators. Populated by attributing
   *  global `usage` events to the currently-active sub-agent scope. */
  tokensInput: number;
  tokensOutput: number;
  tokensCacheRead: number;
  tokensCacheCreation: number;
}

export interface LiveActivityState {
  /** session_runs.id of the active run, or null when not streaming. */
  runId: string | null;
  /** Whether the SSE stream is open right now (vs reattached cache). */
  live: boolean;
  /** ms timestamp of the most recent progress / event we received. */
  lastProgressAtMs: number;
  /** Wall-clock epoch ms when this run started. */
  startedAtMs: number;
  /** Token accumulators (server cumulative, never decreases). */
  tokensInput: number;
  tokensOutput: number;
  tokensCacheRead: number;
  tokensCacheCreation: number;
  toolCallsCount: number;
  subAgentsActive: number;
  subAgentsCompleted: number;
  currentActivity: string;
  /** Vendor for the run (badge in expanded view). */
  vendor: 'anthropic' | 'openai' | 'google' | null;
  /** Selected model alias for the badge. */
  model: string | null;
  /** Effort tier for the badge. */
  effort: string | null;
  /** permission_mode for the badge. */
  permissionMode: string | null;
  /** Container name (for the metadata block). */
  containerName: string | null;
  /** Last N tool calls (cap at TOOL_CALLS_CAP to keep DOM lean). */
  toolCalls: ToolCallEntry[];
  /** Sub-agents tree (Anthropic-only today). */
  subAgents: SubAgentEntry[];
}

export const EMPTY_LIVE_ACTIVITY: LiveActivityState = {
  runId: null,
  live: false,
  lastProgressAtMs: 0,
  startedAtMs: 0,
  tokensInput: 0,
  tokensOutput: 0,
  tokensCacheRead: 0,
  tokensCacheCreation: 0,
  toolCallsCount: 0,
  subAgentsActive: 0,
  subAgentsCompleted: 0,
  currentActivity: '',
  vendor: null,
  model: null,
  effort: null,
  permissionMode: null,
  containerName: null,
  toolCalls: [],
  subAgents: [],
};

const TOOL_CALLS_CAP = 50; // hard cap on the visible tail
/** No-progress window after which a live run is treated as hung/stalled.
 *  Exported so SessionActivityDock derives the same "Stalled" threshold. */
export const STALE_THRESHOLD_MS = 10_000;

// ── Helpers ───────────────────────────────────────────────────────────

export function formatTimer(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  const mm = String(minutes).padStart(minutes >= 10 ? 2 : 1, '0');
  const ss = String(seconds).padStart(2, '0');
  return `${mm}:${ss}`;
}

export function formatTokenCount(n: number): string {
  if (n >= 1_000_000) {
    return `${(n / 1_000_000).toFixed(1).replace(/\.0$/, '')}M`;
  }
  if (n >= 10_000) {
    return `${(n / 1_000).toFixed(1).replace(/\.0$/, '')}k`;
  }
  if (n >= 1_000) {
    return `${(n / 1_000).toFixed(2).replace(/\.?0+$/, '')}k`;
  }
  return n.toLocaleString('en-US');
}

/**
 * Total tokens consumed: input (which Anthropic re-bills each turn) +
 * output + cache_creation. We deliberately exclude cache_read because
 * those are cheap reads of an already-billed prefix; counting them
 * would over-state the "tokens burned" number the founder asked for.
 */
export function totalBurned(s: LiveActivityState): number {
  return s.tokensInput + s.tokensOutput + s.tokensCacheCreation;
}

/**
 * Hydrate a fresh state from a /runs/active row. Used on tab reattach.
 */
export function hydrateFromRunRow(run: SessionRunInfo): LiveActivityState {
  return {
    ...EMPTY_LIVE_ACTIVITY,
    runId: run.id,
    live: run.status === 'running' || run.status === 'starting',
    lastProgressAtMs: Date.parse(run.lastHeartbeatAt) || Date.now(),
    startedAtMs: Date.parse(run.startedAt) || Date.now(),
    tokensInput: run.tokensInput ?? 0,
    tokensOutput: run.tokensOutput ?? 0,
    tokensCacheRead: run.tokensCacheRead ?? 0,
    tokensCacheCreation: run.tokensCacheCreation ?? 0,
    toolCallsCount: run.toolCallsCount ?? 0,
    subAgentsActive: 0,
    subAgentsCompleted: run.subagentsSpawned ?? 0,
    currentActivity: 'Reconnecting…',
    vendor: run.vendor ?? null,
    model: run.model,
    effort: run.effort,
    permissionMode: run.permissionMode,
    containerName: run.containerName ?? null,
    toolCalls: [],
    subAgents: [],
  };
}

// ── Reducer ───────────────────────────────────────────────────────────

export type LiveActivityAction =
  | { type: 'reset' }
  | { type: 'hydrate'; state: LiveActivityState }
  | { type: 'run_started'; runId: string; vendor: 'anthropic' | 'openai' | 'google' | null; model: string | null; effort: string | null; permissionMode: string | null; containerName: string | null }
  | {
      type: 'progress';
      runId: string;
      elapsedMs: number;
      tokensInput: number;
      tokensOutput: number;
      tokensCacheRead: number;
      tokensCacheCreation: number;
      subAgentsActive: number;
      subAgentsCompleted: number;
      toolCallsCount: number;
      currentActivity: string;
    }
  | { type: 'usage'; inputTokens?: number; outputTokens?: number; cacheReadTokens?: number; cacheCreationTokens?: number }
  | { type: 'tool_started'; id: string; name: string; inputPreview: string }
  | { type: 'tool_completed'; toolUseId: string; ok: boolean }
  | { type: 'subagent_started'; parentToolUseId: string; subagentType: string | null; description: string | null }
  | { type: 'subagent_completed'; parentToolUseId: string; ok: boolean }
  | { type: 'done' };

export function liveActivityReducer(
  state: LiveActivityState,
  action: LiveActivityAction,
): LiveActivityState {
  switch (action.type) {
    case 'reset':
      return EMPTY_LIVE_ACTIVITY;
    case 'hydrate':
      return action.state;
    case 'run_started': {
      const now = Date.now();
      return {
        ...EMPTY_LIVE_ACTIVITY,
        runId: action.runId,
        live: true,
        lastProgressAtMs: now,
        startedAtMs: now,
        currentActivity: 'Starting…',
        vendor: action.vendor,
        model: action.model,
        effort: action.effort,
        permissionMode: action.permissionMode,
        containerName: action.containerName,
      };
    }
    case 'progress': {
      // Counters are cumulative on the server. Use max() defensively so
      // a late-arriving frame can't roll counters backwards.
      return {
        ...state,
        lastProgressAtMs: Date.now(),
        tokensInput: Math.max(state.tokensInput, action.tokensInput),
        tokensOutput: Math.max(state.tokensOutput, action.tokensOutput),
        tokensCacheRead: Math.max(state.tokensCacheRead, action.tokensCacheRead),
        tokensCacheCreation: Math.max(
          state.tokensCacheCreation,
          action.tokensCacheCreation,
        ),
        subAgentsActive: action.subAgentsActive,
        subAgentsCompleted: Math.max(state.subAgentsCompleted, action.subAgentsCompleted),
        toolCallsCount: Math.max(state.toolCallsCount, action.toolCallsCount),
        currentActivity: action.currentActivity || state.currentActivity,
      };
    }
    case 'usage': {
      // Global token accumulators (cumulative, never decreases).
      const newInput =
        typeof action.inputTokens === 'number'
          ? Math.max(state.tokensInput, action.inputTokens)
          : state.tokensInput;
      const newOutput =
        typeof action.outputTokens === 'number'
          ? Math.max(state.tokensOutput, action.outputTokens)
          : state.tokensOutput;
      const newCacheRead =
        typeof action.cacheReadTokens === 'number'
          ? Math.max(state.tokensCacheRead, action.cacheReadTokens)
          : state.tokensCacheRead;
      const newCacheCreation =
        typeof action.cacheCreationTokens === 'number'
          ? Math.max(state.tokensCacheCreation, action.cacheCreationTokens)
          : state.tokensCacheCreation;

      // M-X11-fix: attribute the deltas from this usage event to the
      // currently-running sub-agent (if any). The backend emits cumulative
      // global totals, so we take the difference vs. the previous state.
      // This is an approximation: we attribute all tokens consumed between
      // two usage events to the sub-agent that was active during that window.
      const deltaInput = newInput - state.tokensInput;
      const deltaOutput = newOutput - state.tokensOutput;
      const deltaCacheRead = newCacheRead - state.tokensCacheRead;
      const deltaCacheCreation = newCacheCreation - state.tokensCacheCreation;
      const hasDeltas = deltaInput > 0 || deltaOutput > 0 || deltaCacheRead > 0 || deltaCacheCreation > 0;

      let subAgents = state.subAgents;
      if (hasDeltas && state.subAgents.some((s) => s.status === 'running')) {
        // Credit deltas to the last-started running sub-agent.
        const runningIdx = state.subAgents.map((s, i) => ({ s, i }))
          .filter(({ s }) => s.status === 'running')
          .reduce((best, cur) => (cur.s.startedAtMs >= best.s.startedAtMs ? cur : best)).i;
        subAgents = state.subAgents.map((s, i) =>
          i === runningIdx
            ? {
                ...s,
                tokensInput: s.tokensInput + deltaInput,
                tokensOutput: s.tokensOutput + deltaOutput,
                tokensCacheRead: s.tokensCacheRead + deltaCacheRead,
                tokensCacheCreation: s.tokensCacheCreation + deltaCacheCreation,
              }
            : s,
        );
      }

      return {
        ...state,
        lastProgressAtMs: Date.now(),
        tokensInput: newInput,
        tokensOutput: newOutput,
        tokensCacheRead: newCacheRead,
        tokensCacheCreation: newCacheCreation,
        subAgents,
      };
    }
    case 'tool_started': {
      const now = Date.now();
      const next = state.toolCalls.slice();
      next.push({
        id: action.id,
        name: action.name,
        inputPreview: action.inputPreview,
        status: 'running',
        startedAtMs: now,
        endedAtMs: null,
      });
      // Keep the tail bounded.
      while (next.length > TOOL_CALLS_CAP) next.shift();
      return {
        ...state,
        lastProgressAtMs: now,
        toolCalls: next,
        currentActivity: `Calling tool: ${action.name}`,
      };
    }
    case 'tool_completed': {
      const now = Date.now();
      const next = state.toolCalls.map((t) =>
        t.id === action.toolUseId
          ? { ...t, status: action.ok ? ('ok' as const) : ('error' as const), endedAtMs: now }
          : t,
      );
      return { ...state, lastProgressAtMs: now, toolCalls: next };
    }
    case 'subagent_started': {
      const now = Date.now();
      const subAgents = state.subAgents.slice();
      subAgents.push({
        parentToolUseId: action.parentToolUseId,
        subagentType: action.subagentType,
        description: action.description,
        status: 'running',
        startedAtMs: now,
        endedAtMs: null,
        tokensInput: 0,
        tokensOutput: 0,
        tokensCacheRead: 0,
        tokensCacheCreation: 0,
      });
      return {
        ...state,
        lastProgressAtMs: now,
        subAgents,
        subAgentsActive: state.subAgentsActive + 1,
        currentActivity: action.subagentType
          ? `Sub-agent: ${action.subagentType}`
          : 'Sub-agent: launching…',
      };
    }
    case 'subagent_completed': {
      const now = Date.now();
      const subAgents = state.subAgents.map((s) =>
        s.parentToolUseId === action.parentToolUseId
          ? { ...s, status: action.ok ? ('ok' as const) : ('error' as const), endedAtMs: now }
          : s,
      );
      return {
        ...state,
        lastProgressAtMs: now,
        subAgents,
        subAgentsActive: Math.max(0, state.subAgentsActive - 1),
        subAgentsCompleted: state.subAgentsCompleted + 1,
      };
    }
    case 'done':
      return { ...state, live: false, currentActivity: 'Done' };
    default:
      return state;
  }
}

// ── Component ────────────────────────────────────────────────────────

export interface LiveActivityPanelProps {
  state: LiveActivityState;
  /** When true, render compact one-line mode (mobile). */
  compact?: boolean;
  className?: string;
}

/**
 * @deprecated Retired 2026-07-07 in favour of SessionActivityDock (an
 * always-mounted, bottom-pinned dock that fixes the mount flicker
 * structurally). No longer rendered by SessionChatPane; kept here only as a
 * reference implementation and to keep the shared renderers exercised. The
 * body renderers (RunMetadata / SubAgentStack / ToolCallList) are now exported
 * and reused by the dock.
 */
export function LiveActivityPanel({
  state,
  compact = false,
  className,
}: LiveActivityPanelProps): React.JSX.Element | null {
  // Don't render at all when there's nothing to show. The parent
  // controls visibility, but a defensive null keeps stray renders from
  // stealing space at the top of the chat list.
  if (!state.runId && !state.live) return null;

  const [expanded, setExpanded] = React.useState(false);
  const [now, setNow] = React.useState<number>(() => Date.now());

  // Tick once per second so the timer label updates. We use a single
  // top-level interval (cheaper than reactive timers per child).
  React.useEffect(() => {
    if (!state.live) return;
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [state.live]);

  const elapsedMs = state.startedAtMs ? Math.max(0, now - state.startedAtMs) : 0;
  const sinceProgressMs = state.lastProgressAtMs
    ? Math.max(0, now - state.lastProgressAtMs)
    : 0;
  const isStale = state.live && sinceProgressMs > STALE_THRESHOLD_MS;
  const isThinking =
    state.live && /thinking/i.test(state.currentActivity);
  const isDone = !state.live;

  const burned = totalBurned(state);

  const dotState: 'live' | 'thinking' | 'stale' | 'idle' = isDone
    ? 'idle'
    : isStale
      ? 'stale'
      : isThinking
        ? 'thinking'
        : 'live';

  return (
    <div
      className={cn(
        'mb-2 rounded-lg border border-border bg-card/40 backdrop-blur-sm',
        'transition-colors',
        isStale && 'border-destructive/50 bg-destructive/5',
        className,
      )}
      data-testid="live-activity-panel"
      data-state={dotState}
    >
      {/* Compact one-liner */}
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="flex w-full items-center gap-2 px-3 py-2 text-[12px] hover:bg-muted/30 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        aria-expanded={expanded}
        aria-controls="live-activity-details"
        data-testid="live-activity-toggle"
      >
        <PulseDot state={dotState} />
        <span
          className={cn(
            'font-medium tracking-tight',
            isStale ? 'text-destructive' : 'text-foreground',
          )}
          data-testid="live-activity-status"
        >
          {isDone
            ? 'Done'
            : isStale
              ? 'Stalled — no progress in 10s'
              : state.currentActivity || 'Agent working…'}
        </span>
        {/* Timer */}
        <span
          className={cn(
            'ml-3 inline-flex items-center gap-1 tabular-nums text-muted-foreground',
          )}
          data-testid="live-activity-timer"
        >
          <Clock className="h-3 w-3" aria-hidden />
          {formatTimer(elapsedMs)}
        </span>
        {/* Token counter */}
        <TokenBadge
          burned={burned}
          tokensInput={state.tokensInput}
          tokensOutput={state.tokensOutput}
          tokensCacheRead={state.tokensCacheRead}
          tokensCacheCreation={state.tokensCacheCreation}
        />
        {/* Tool calls + sub-agent indicator on the right */}
        {!compact && (state.toolCallsCount > 0 || state.subAgentsActive > 0) && (
          <span
            className="ml-2 inline-flex items-center gap-2 text-[11px] text-muted-foreground"
            data-testid="live-activity-tool-summary"
          >
            {state.toolCallsCount > 0 && (
              <span className="inline-flex items-center gap-0.5">
                <Wrench className="h-3 w-3" aria-hidden />
                {state.toolCallsCount}
              </span>
            )}
            {(state.subAgentsActive > 0 || state.subAgentsCompleted > 0) && (
              <span className="inline-flex items-center gap-0.5">
                <GitBranch className="h-3 w-3" aria-hidden />
                {state.subAgentsActive > 0
                  ? `${state.subAgentsActive}↑`
                  : state.subAgentsCompleted}
              </span>
            )}
          </span>
        )}
        <span className="ml-auto inline-flex items-center text-muted-foreground">
          {expanded ? (
            <ChevronDown className="h-3.5 w-3.5" aria-hidden />
          ) : (
            <ChevronRight className="h-3.5 w-3.5" aria-hidden />
          )}
        </span>
      </button>
      {expanded && (
        <div
          id="live-activity-details"
          className="border-t border-border/60 px-3 pb-3 pt-2 text-[12px]"
          data-testid="live-activity-details"
        >
          <RunMetadata state={state} />
          <SubAgentStack subAgents={state.subAgents} state={state} now={now} />
          <ToolCallList toolCalls={state.toolCalls} now={now} />
        </div>
      )}
    </div>
  );
}

// ── PulseDot ─────────────────────────────────────────────────────────
function PulseDot({
  state,
}: {
  state: 'live' | 'thinking' | 'stale' | 'idle';
}): React.JSX.Element {
  return (
    <span className="relative inline-flex h-2.5 w-2.5 shrink-0" aria-hidden>
      {/* Outer pulse ring (only when active) */}
      {(state === 'live' || state === 'thinking') && (
        <span
          className={cn(
            'absolute inset-0 inline-flex rounded-full opacity-60',
            state === 'live'
              ? 'bg-emerald-400 animate-ping'
              : 'bg-amber-400 animate-pulse',
          )}
        />
      )}
      <span
        className={cn(
          'relative inline-flex h-2.5 w-2.5 rounded-full',
          state === 'live' && 'bg-emerald-500',
          state === 'thinking' && 'bg-amber-500',
          state === 'stale' && 'bg-destructive',
          state === 'idle' && 'bg-muted-foreground/60',
        )}
      />
    </span>
  );
}

// ── TokenBadge ────────────────────────────────────────────────────────
function TokenBadge({
  burned,
  tokensInput,
  tokensOutput,
  tokensCacheRead,
  tokensCacheCreation,
}: {
  burned: number;
  tokensInput: number;
  tokensOutput: number;
  tokensCacheRead: number;
  tokensCacheCreation: number;
}): React.JSX.Element {
  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            className="ml-3 inline-flex items-center gap-1 tabular-nums text-muted-foreground"
            data-testid="live-activity-tokens"
          >
            <Cpu className="h-3 w-3" aria-hidden />
            {formatTokenCount(burned)} tokens
          </span>
        </TooltipTrigger>
        <TooltipContent side="top" align="start" className="max-w-[260px]">
          <div className="flex flex-col gap-0.5 text-[11px] leading-snug">
            <div>
              <span className="text-muted-foreground">Input: </span>
              <span className="font-medium tabular-nums">
                {tokensInput.toLocaleString('en-US')}
              </span>
            </div>
            <div>
              <span className="text-muted-foreground">Output: </span>
              <span className="font-medium tabular-nums">
                {tokensOutput.toLocaleString('en-US')}
              </span>
            </div>
            {tokensCacheCreation > 0 && (
              <div>
                <span className="text-muted-foreground">Cache write: </span>
                <span className="font-medium tabular-nums">
                  {tokensCacheCreation.toLocaleString('en-US')}
                </span>
              </div>
            )}
            {tokensCacheRead > 0 && (
              <div>
                <span className="text-muted-foreground">Cache read: </span>
                <span className="font-medium tabular-nums">
                  {tokensCacheRead.toLocaleString('en-US')}
                </span>
                <span className="text-muted-foreground/80"> (free)</span>
              </div>
            )}
            <div className="mt-1 border-t border-border pt-1">
              <span className="text-muted-foreground">Burned: </span>
              <span className="font-medium tabular-nums">
                {(tokensInput + tokensOutput + tokensCacheCreation).toLocaleString(
                  'en-US',
                )}
              </span>
            </div>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

// ── Expanded: Run metadata ────────────────────────────────────────────
// Exported for reuse by SessionActivityDock (the always-mounted replacement).
export function RunMetadata({ state }: { state: LiveActivityState }): React.JSX.Element {
  return (
    <div className="mb-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-muted-foreground">
      {state.vendor && (
        <span className="inline-flex items-center gap-1 rounded bg-muted/60 px-1.5 py-0.5">
          <Bot className="h-3 w-3" aria-hidden />
          {VENDOR_LABEL[state.vendor]}
        </span>
      )}
      {state.model && <span>Model: {state.model}</span>}
      {/* 2026-10-04: the vendor's own name for the level ("Extra high" on
          GPT, "xHigh" on Claude), never the raw token. */}
      {state.effort && <span>Effort: {effortLabel(state.vendor, state.effort)}</span>}
      {state.permissionMode && <span>Perm: {state.permissionMode}</span>}
      {state.containerName && (
        <span className="truncate font-mono">{state.containerName}</span>
      )}
    </div>
  );
}

const VENDOR_LABEL: Record<'anthropic' | 'openai' | 'google', string> = {
  anthropic: 'Claude',
  openai: 'GPT',
  google: 'Gemini',
};

// ── Expanded: Sub-agent stack ────────────────────────────────────────
// Exported for reuse by SessionActivityDock (the always-mounted replacement).
export function SubAgentStack({
  subAgents,
  state,
  now,
}: {
  subAgents: SubAgentEntry[];
  state: LiveActivityState;
  now: number;
}): React.JSX.Element | null {
  if (subAgents.length === 0) return null;
  return (
    <div className="mb-3" data-testid="live-activity-subagent-stack">
      <div className="mb-1 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
        Sub-agents
      </div>
      <ul className="flex flex-col gap-1.5 pl-3">
        <li className="flex items-center gap-2 text-[11px]">
          <span
            className={cn(
              'h-1.5 w-1.5 shrink-0 rounded-full',
              state.live ? 'bg-emerald-500' : 'bg-muted-foreground/60',
            )}
            aria-hidden
          />
          <span className="font-medium text-foreground">Main agent</span>
          <span className="text-muted-foreground">
            ({formatTimer(state.startedAtMs ? now - state.startedAtMs : 0)})
          </span>
        </li>
        {subAgents.map((s, idx) => {
          const elapsed = s.endedAtMs ? s.endedAtMs - s.startedAtMs : now - s.startedAtMs;
          const subTotal = s.tokensInput + s.tokensOutput + s.tokensCacheCreation;
          const hasSubTokens = subTotal > 0;
          return (
            <li
              key={s.parentToolUseId || idx}
              className="ml-3 flex flex-col gap-0.5 text-[11px]"
              data-testid="live-activity-subagent-row"
            >
              <div className="flex items-center gap-2">
                <span
                  className={cn(
                    'h-1.5 w-1.5 shrink-0 rounded-full',
                    s.status === 'running' && 'bg-emerald-500',
                    s.status === 'ok' && 'bg-muted-foreground/60',
                    s.status === 'error' && 'bg-destructive',
                  )}
                  aria-hidden
                />
                <span className="font-medium text-foreground">
                  {s.subagentType ?? 'Sub-agent'}
                </span>
                {s.description && (
                  <span className="truncate text-muted-foreground">— {s.description}</span>
                )}
                <span className="ml-auto tabular-nums text-muted-foreground">
                  {formatTimer(elapsed)}
                </span>
                <span
                  className={cn(
                    'rounded px-1 text-[10px]',
                    s.status === 'running' && 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300',
                    s.status === 'ok' && 'bg-muted text-muted-foreground',
                    s.status === 'error' && 'bg-destructive/10 text-destructive',
                  )}
                >
                  {s.status === 'running' ? (
                    <Loader2 className="inline h-2.5 w-2.5 animate-spin" aria-hidden />
                  ) : (
                    s.status
                  )}
                </span>
              </div>
              {/* M-X11-fix: per-sub-agent token breakdown */}
              {hasSubTokens && (
                <div
                  className="ml-3.5 tabular-nums text-[10px] text-muted-foreground"
                  data-testid="live-activity-subagent-tokens"
                >
                  <Cpu className="mr-0.5 inline h-2.5 w-2.5" aria-hidden />
                  {subTotal.toLocaleString()} tokens
                  <span className="ml-1 opacity-70">
                    (in:&nbsp;{s.tokensInput.toLocaleString()} · out:&nbsp;{s.tokensOutput.toLocaleString()})
                  </span>
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

// ── Expanded: Tool calls list ─────────────────────────────────────────
// Exported for reuse by SessionActivityDock (the always-mounted replacement).
export function ToolCallList({
  toolCalls,
  now,
}: {
  toolCalls: ToolCallEntry[];
  now: number;
}): React.JSX.Element | null {
  const [openId, setOpenId] = React.useState<string | null>(null);
  if (toolCalls.length === 0) return null;
  // Show newest first.
  const ordered = toolCalls.slice().reverse().slice(0, 20);
  return (
    <div data-testid="live-activity-toolcalls">
      <div className="mb-1 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
        Recent tool calls ({toolCalls.length})
      </div>
      <ul className="flex flex-col gap-1">
        {ordered.map((t) => {
          const elapsed = t.endedAtMs ? t.endedAtMs - t.startedAtMs : now - t.startedAtMs;
          const open = openId === t.id;
          return (
            <li
              key={t.id}
              className="rounded border border-border/50 bg-background/40 px-2 py-1 text-[11px]"
              data-testid="live-activity-tool-row"
            >
              <button
                type="button"
                onClick={() => setOpenId(open ? null : t.id)}
                className="flex w-full items-center gap-2 text-left"
              >
                <Hammer className="h-3 w-3 shrink-0 text-muted-foreground" aria-hidden />
                <span className="font-mono">{t.name}</span>
                <span className="truncate text-muted-foreground">{t.inputPreview}</span>
                <span className="ml-auto tabular-nums text-muted-foreground">
                  {formatTimer(elapsed)}
                </span>
                <span
                  className={cn(
                    'rounded px-1 text-[10px]',
                    t.status === 'running' && 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300',
                    t.status === 'ok' && 'bg-muted text-muted-foreground',
                    t.status === 'error' && 'bg-destructive/10 text-destructive',
                  )}
                >
                  {t.status === 'running' ? (
                    <Loader2 className="inline h-2.5 w-2.5 animate-spin" aria-hidden />
                  ) : (
                    t.status
                  )}
                </span>
              </button>
              {open && t.inputPreview && (
                // WHAT THIS BLOCK ACTUALLY HOLDS — traced to the gateway, not
                // assumed. `inputPreview` is `input_preview` from the SSE
                // `tool_started` event (lib/api/session-chat.ts:404 →
                // session-chat-pane.tsx:2337 → live-activity-panel.tsx:344),
                // which the gateway produces with `safeInputPreview(input, 280)`
                // (session-chat-interactive.ts:3886 and session-chat.ts:497 —
                // byte-identical in both backends):
                //
                //     s = typeof input === 'string' ? input : JSON.stringify(input)
                //     s = s.replace(/\s+/g, ' ').trim()
                //     if (s.length > 280) s = s.slice(0, 279) + '…'
                //
                // i.e. a WHITESPACE-COLLAPSED, SINGLE-LINE, ≤280-character
                // preview. It is NOT the tool input, and it can never contain a
                // newline. The button is labelled "Copy tool preview" for exactly
                // that reason: "Copy tool input" promised the 50 KB Bash command
                // or the 500 KB diff that this preview exists to keep off the wire.
                //
                // Why it still needs a copy button: a <pre> is `white-space: pre`,
                // so those 280 characters are ONE line that does not wrap — it runs
                // off the right edge of a ~300px panel and the box scrolls
                // SIDEWAYS. The clip that defeats a drag-select here is horizontal.
                // (`max-h-32` = 128px is never reached by a single 10px line; the
                // earlier comment claiming a vertical clip was wrong.)
                //
                // The button is a SIBLING of the <pre>, never inside the header
                // <button> (a button in a button is invalid HTML and React will not
                // render it), and absolutely positioned so it costs no height;
                // `pr-8` keeps the text from running under it.
                <div className="relative mt-2">
                  <pre
                    className="max-h-32 overflow-auto rounded bg-muted/30 p-2 pr-8 text-[10px] leading-snug"
                    data-testid="live-activity-tool-preview"
                  >
                    {t.inputPreview}
                  </pre>
                  <CopyButton
                    text={t.inputPreview}
                    label="Copy tool preview"
                    // 24px box, ±10px on coarse ⇒ a real 44 × 44. The vertical
                    // arithmetic, stated because the previous version of this
                    // comment asserted a clearance it had not computed:
                    //
                    //   ABOVE  wrapper `mt-2` (8px) + `top-1` (4px) − 10px inset
                    //          = +2px clear of the header <button>. It used to be
                    //          `mt-1`: 4 + 4 − 10 = −2px, i.e. the invisible box
                    //          reached 2px INTO the expander across its full width,
                    //          and CopyButton is `relative`, so it hit-tested ABOVE
                    //          that unpositioned button — a tap on the expander's
                    //          bottom edge copied instead of collapsing.
                    //   BELOW  4 + 24 + 10 = 38px from the wrapper top. The
                    //          shortest possible <pre> is p-2 (16) + one 10px line
                    //          at leading-snug (13.75) = 29.75px, so the box can
                    //          reach 8.25px past the wrapper — into the li's py-1
                    //          (4) + the ul's gap-1 (4) + the next li's py-1 (4)
                    //          = 12px of dead space. +3.75px clear.
                    //   RIGHT  `right-1` (4) + 10 = 14px, against 8px of li px-2
                    //          plus 12px of dock-body px-3 = 20px before the
                    //          nearest clip (the live mount is
                    //          session-activity-dock.tsx:287, whose body is
                    //          `overflow-y-auto` — which clips BOTH axes). Clear.
                    //
                    // Nothing else on the path clips: the wrapper is `relative`
                    // only, <li>/<ul> declare no overflow, and the button is a
                    // sibling of the scrolling <pre>, not inside it.
                    className="absolute right-1 top-1 h-6 w-6 bg-background/70 pointer-coarse:after:-inset-x-2.5 pointer-coarse:after:-inset-y-2.5"
                    iconClassName="h-3 w-3"
                    data-testid="live-activity-tool-copy"
                  />
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
